import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  DEFAULT_SUBTITLE_TEXT_TRANSFORM,
  SUBTITLE_TEXT_TRANSFORMS,
  type SubtitleTextTransform,
} from '@/lib/subtitles/subtitleTextTransform';
import { cn } from '@/lib/utils';

const TRANSFORM_LABEL_KEYS: Record<SubtitleTextTransform, string> = {
  none: 'configureSubtitles.editor.textTransformNone',
  uppercase: 'configureSubtitles.editor.textTransformUppercase',
  lowercase: 'configureSubtitles.editor.textTransformLowercase',
  'small-caps': 'configureSubtitles.editor.textTransformSmallCaps',
};

const TRANSFORM_TOOLTIP_KEYS: Record<SubtitleTextTransform, string> = {
  none: 'configureSubtitles.editor.textTransformNoneTooltip',
  uppercase: 'configureSubtitles.editor.textTransformUppercaseTooltip',
  lowercase: 'configureSubtitles.editor.textTransformLowercaseTooltip',
  'small-caps': 'configureSubtitles.editor.textTransformSmallCapsTooltip',
};

interface SubtitleTextTransformControlProps {
  value?: SubtitleTextTransform;
  onChange: (value: SubtitleTextTransform) => void;
  className?: string;
}

export const SubtitleTextTransformControl = ({
  value = DEFAULT_SUBTITLE_TEXT_TRANSFORM,
  onChange,
  className,
}: SubtitleTextTransformControlProps) => {
  const { t } = useTranslation();

  return (
    <div className={cn('space-y-1', className)}>
      <Label className="text-xs text-muted-foreground">
        {t('configureSubtitles.editor.textTransform')}
      </Label>
      <TooltipProvider delayDuration={150}>
        <div className="grid grid-cols-4 gap-0.5 rounded-md bg-muted/50 p-0.5">
          {SUBTITLE_TEXT_TRANSFORMS.map((transform) => (
            <Tooltip key={transform}>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant={value === transform ? 'default' : 'ghost'}
                  size="sm"
                  className={cn(
                    'h-7 px-1 text-[10px] font-semibold leading-none',
                    transform === 'small-caps' && value !== transform && 'capitalize [font-variant:small-caps]',
                  )}
                  data-testid={`subtitle-text-transform-${transform}`}
                  aria-label={t(TRANSFORM_TOOLTIP_KEYS[transform])}
                  onClick={() => onChange(transform)}
                >
                  {t(TRANSFORM_LABEL_KEYS[transform])}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top" align="center" className="max-w-[220px] text-center">
                {t(TRANSFORM_TOOLTIP_KEYS[transform])}
              </TooltipContent>
            </Tooltip>
          ))}
        </div>
      </TooltipProvider>
    </div>
  );
};
